import type {
    CreateVerificationRequest,
    DocsVerificationEventsModel,
    DocsVerificationResultModel,
    EditVerificationStatusRequest,
    ProceedVerificationRequest,
    SearchVerificationRequest,
    VerificationIdRequest,
    VerificationStatusManualChangesModel,
    VerificationStatusManualChangesRequest,
} from '@models';
import { ApiTagEnum } from '@models/enums';
import type { PaginationListResponse } from '@models/types';

import { dashboardApi } from './dashboard-base-api';

const URL_PREFIX = 'docver/v2/';
const URL_PREFIX_NEW = '/webapi/v4/docver/';

export const verificationsApiSlice = dashboardApi.injectEndpoints({
    endpoints: (build) => ({
        getVerificationsList: build.query<
            PaginationListResponse<DocsVerificationResultModel>,
            SearchVerificationRequest
        >({
            query: ({ organizationId, limit = 10, ...params }) => ({
                url: `${URL_PREFIX_NEW}organization/${organizationId}/verifications`,
                params: {
                    limit,
                    ...params,
                },
            }),
            providesTags: [ApiTagEnum.VERIFICATION],
        }),
        getVerificationById: build.query<DocsVerificationResultModel, VerificationIdRequest>({
            query: ({ organizationId, verificationId }) =>
                `${URL_PREFIX_NEW}organization/${organizationId}/verifications/${verificationId}`,
        }),
        getVerificationByShortId: build.query<
            { id: string },
            { organizationId: string; id: string }
        >({
            query: ({ organizationId, id }) =>
                `${URL_PREFIX}${organizationId}/verifications/short/${id}/id`,
        }),
        createVerification: build.mutation<DocsVerificationResultModel, CreateVerificationRequest>({
            query({ body, params }) {
                return {
                    url: `${URL_PREFIX}${params.organizationId}/verifications`,
                    method: 'POST',
                    body,
                };
            },
        }),
        proceedVerification: build.mutation<void, ProceedVerificationRequest>({
            query({ params }) {
                return {
                    url: `${URL_PREFIX}${params.organizationId}/verifications/${params.verificationId}/proceed`,
                    method: 'POST',
                };
            },
        }),
        cancelVerification: build.mutation<void, ProceedVerificationRequest>({
            query({ params }) {
                return {
                    url: `${URL_PREFIX}${params.organizationId}/verifications/${params.verificationId}/cancel`,
                    method: 'POST',
                };
            },
        }),
        editVerificationStatus: build.mutation<void, EditVerificationStatusRequest>({
            query({ params, body }) {
                return {
                    url: `${URL_PREFIX}${params.organizationId}/verifications/${params.verificationId}/set_status`,
                    method: 'POST',
                    body,
                };
            },
        }),
        getVerificationStatusManualChanges: build.mutation<
            PaginationListResponse<VerificationStatusManualChangesModel>,
            VerificationStatusManualChangesRequest
        >({
            query({ params }) {
                return {
                    url: `${URL_PREFIX}${params.organizationId}/verifications/${params.verificationId}/manual_changes`,
                    method: 'GET',
                };
            },
        }),
        getVerificationEvents: build.query<DocsVerificationEventsModel[], VerificationIdRequest>({
            query: ({ organizationId, verificationId }) =>
                `${URL_PREFIX_NEW}/${organizationId}/verifications/${verificationId}/events`,
        }),
    }),
});
export const {
    useLazyGetVerificationsListQuery,
    useCreateVerificationMutation,
    useLazyGetVerificationByIdQuery,
    useLazyGetVerificationByShortIdQuery,
    useCancelVerificationMutation,
    useEditVerificationStatusMutation,
    useGetVerificationStatusManualChangesMutation,
    useLazyGetVerificationEventsQuery,
} = verificationsApiSlice;
